/**
 * React hook for managing favorites with localStorage persistence and cloud sync
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  FavoriteItem,
  FavoriteType,
  AddFavoritePayload,
  UseFavoritesReturn
} from '@/types/favorites';
import {
  saveFavorites,
  loadFavorites,
  addFavorite as addFavoriteToStorage,
  removeFavorite as removeFavoriteFromStorage,
  clearFavorites as clearFavoritesFromStorage,
  exportFavorites as exportFavoritesFromStorage,
  importFavorites as importFavoritesFromStorage,
  createFavoriteItem,
} from '@/lib/favorites-storage';
import { useOptionalAuth } from './use-auth';
import {
  isFirestoreAvailable,
  getCloudFavorites,
  saveCloudFavorites,
  subscribeToFavorites,
  generateDeviceId,
} from '@/lib/firestore';
import { resolveFavoritesConflict } from '@/lib/conflict-resolution';

export interface UseFavoritesWithSyncReturn extends UseFavoritesReturn {
  isSyncing: boolean;
  syncError: string | null;
  lastSyncedAt: Date | null;
  syncNow: () => Promise<void>;
  isCloudEnabled: boolean;
}

const SYNC_DEBOUNCE_MS = 3000;
const LOCAL_VERSION_KEY = 'v2v2-favorites-version';

function getLocalVersion(): number {
  try {
    return parseInt(localStorage.getItem(LOCAL_VERSION_KEY) || '0', 10);
  } catch {
    return 0;
  }
}

function setLocalVersion(version: number): void {
  try {
    localStorage.setItem(LOCAL_VERSION_KEY, version.toString());
  } catch {
    console.warn('Failed to save local version');
  }
}

export function useFavorites(): UseFavoritesWithSyncReturn {
  const auth = useOptionalAuth();
  const [favorites, setFavorites] = useState<FavoriteItem[]>([]);
  const [, setIsLoading] = useState(true);
  const [, setError] = useState<string | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [lastSyncedAt, setLastSyncedAt] = useState<Date | null>(null);

  const syncTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const localVersionRef = useRef(getLocalVersion());
  const isMountedRef = useRef(true);

  const isCloudEnabled = useMemo(
    () => isFirestoreAvailable() && auth?.isAuthenticated === true && auth?.user !== null,
    [auth?.isAuthenticated, auth?.user]
  );

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    try {
      const loaded = loadFavorites();
      setFavorites(loaded);
    } catch (err) {
      setError('Failed to load favorites');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const syncWithCloud = useCallback(async () => {
    if (!isCloudEnabled || !auth?.user?.id) return;

    setIsSyncing(true);
    setSyncError(null);

    try {
      const cloudData = await getCloudFavorites(auth.user.id);
      const localItems = loadFavorites();

      if (!cloudData) {
        await saveCloudFavorites(auth.user.id, localItems, localVersionRef.current + 1);
        localVersionRef.current += 1;
        setLocalVersion(localVersionRef.current);
      } else {
        const result = resolveFavoritesConflict(localItems, cloudData.items, 'merge');

        if (result.hadConflict || cloudData.version > localVersionRef.current) {
          saveFavorites(result.resolved);
          setFavorites(result.resolved);
        }

        if (result.hadConflict || localVersionRef.current >= cloudData.version) {
          const newVersion = Math.max(cloudData.version, localVersionRef.current) + 1;
          await saveCloudFavorites(auth.user.id, result.resolved, newVersion);
          localVersionRef.current = newVersion;
          setLocalVersion(newVersion);
        }
      }

      if (isMountedRef.current) {
        setLastSyncedAt(new Date());
      }
    } catch (err) {
      if (isMountedRef.current) {
        setSyncError(err instanceof Error ? err.message : 'Sync failed');
      }
    } finally {
      if (isMountedRef.current) {
        setIsSyncing(false);
      }
    }
  }, [isCloudEnabled, auth?.user?.id]);

  useEffect(() => {
    if (!isCloudEnabled || !auth?.user?.id) return;

    syncWithCloud();

    const unsubscribe = subscribeToFavorites(auth.user.id, (cloudFavorites) => {
      if (!cloudFavorites || !isMountedRef.current) return;

      const deviceId = generateDeviceId();
      if (cloudFavorites.deviceId === deviceId) return;

      if (cloudFavorites.version > localVersionRef.current) {
        const localItems = loadFavorites();
        const result = resolveFavoritesConflict(localItems, cloudFavorites.items, 'merge');

        saveFavorites(result.resolved);
        setFavorites(result.resolved);
        localVersionRef.current = cloudFavorites.version;
        setLocalVersion(cloudFavorites.version);
        setLastSyncedAt(new Date());
      }
    });

    return () => {
      unsubscribe();
    };
  }, [isCloudEnabled, auth?.user?.id, syncWithCloud]);

  const scheduleSyncDebounced = useCallback(() => {
    if (!isCloudEnabled) return;

    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current);
    }

    syncTimeoutRef.current = setTimeout(() => {
      if (isMountedRef.current) {
        syncWithCloud();
      }
    }, SYNC_DEBOUNCE_MS);
  }, [isCloudEnabled, syncWithCloud]);

  const isFavorite = useCallback((id: string, type: FavoriteType): boolean => {
    return favorites.some((item) => item.id === id && item.type === type);
  }, [favorites]);

  const addFavorite = useCallback((payload: AddFavoritePayload): boolean => {
    const item = createFavoriteItem(payload);
    const success = addFavoriteToStorage(item);

    if (success) {
      setFavorites((prev) => [...prev, item]);
      scheduleSyncDebounced();
    }

    return success;
  }, [scheduleSyncDebounced]);

  const removeFavorite = useCallback((id: string, type: FavoriteType): boolean => {
    const success = removeFavoriteFromStorage(id, type);

    if (success) {
      setFavorites((prev) => prev.filter((item) => !(item.id === id && item.type === type)));
      scheduleSyncDebounced();
    }

    return success;
  }, [scheduleSyncDebounced]);

  const toggleFavorite = useCallback((payload: AddFavoritePayload): boolean => {
    const exists = isFavorite(payload.id, payload.type);

    if (exists) {
      return removeFavorite(payload.id, payload.type);
    } else {
      return addFavorite(payload);
    }
  }, [isFavorite, addFavorite, removeFavorite]);

  const clearFavorites = useCallback(async () => {
    try {
      clearFavoritesFromStorage();
      setFavorites([]);
      localVersionRef.current += 1;
      setLocalVersion(localVersionRef.current);

      if (isCloudEnabled && auth?.user?.id) {
        await saveCloudFavorites(auth.user.id, [], localVersionRef.current);
      }
    } catch (err) {
      setError('Failed to clear favorites');
      throw err;
    }
  }, [isCloudEnabled, auth?.user?.id]);

  const exportFavorites = useCallback((): string => {
    return exportFavoritesFromStorage();
  }, []);

  const importFavorites = useCallback((json: string): { success: boolean; added: number; errors: string[] } => {
    const result = importFavoritesFromStorage(json);

    if (result.success) {
      setFavorites(loadFavorites());
      scheduleSyncDebounced();
    }

    return {
      success: result.success,
      added: result.items.length,
      errors: result.errors,
    };
  }, [scheduleSyncDebounced]);

  const getFavoritesByType = useCallback((type: FavoriteType): FavoriteItem[] => {
    return favorites.filter((item) => item.type === type);
  }, [favorites]);

  const syncNow = useCallback(async () => {
    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current);
    }
    await syncWithCloud();
  }, [syncWithCloud]);

  const counts = useMemo(() => ({
    total: favorites.length,
    market: favorites.filter((item) => item.type === 'market').length,
    vendor: favorites.filter((item) => item.type === 'vendor').length,
    product: favorites.filter((item) => item.type === 'product').length,
  }), [favorites]);

  return {
    favorites,
    isFavorite,
    addFavorite,
    removeFavorite,
    toggleFavorite,
    clearFavorites,
    exportFavorites,
    importFavorites,
    getFavoritesByType,
    totalCount: counts.total,
    marketCount: counts.market,
    vendorCount: counts.vendor,
    productCount: counts.product,
    isSyncing,
    syncError,
    lastSyncedAt,
    syncNow,
    isCloudEnabled,
  };
}
